import {
  Box,
  chakra,
  Heading,
  HStack,
  Tab,
  Table,
  TabList,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { ComponentType, ReactNode, useEffect, useMemo, useState } from 'react';
import { IconProps } from '~components/ui/icons';
import { PageLoader } from '~components/ui/PageLoader';
import { PageSizeSelector } from '~components/ui/PageSizeSelector';
import { PaginationServerSide } from '~components/ui/PaginationServerSide';
import { ServiceAdminOrgAccessRequestFilter } from '~graphql/__generated__/types';
import { accessRequestFilters } from './accessRequestUtils';

const PAGE_SIZES = [8, 25, 50, 100];

const filterLabels: Record<ServiceAdminOrgAccessRequestFilter, string> = {
  [ServiceAdminOrgAccessRequestFilter.All]: 'all',
  [ServiceAdminOrgAccessRequestFilter.Approved]: 'activeSessions',
  [ServiceAdminOrgAccessRequestFilter.Awaiting]: 'awaitingApproval',
  [ServiceAdminOrgAccessRequestFilter.Rejected]: 'rejected',
  [ServiceAdminOrgAccessRequestFilter.Revoked]: 'revoked',
  [ServiceAdminOrgAccessRequestFilter.Expired]: 'expiredStatus',
};

export function TruncatedCell({
  text,
  maxWidth,
  fontWeight,
}: {
  text: string;
  maxWidth: string;
  fontWeight?: string;
}) {
  return (
    <Tooltip label={text} placement="top" openDelay={300}>
      <Box
        maxWidth={maxWidth}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        fontWeight={fontWeight}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

export interface AccessRequestColumn<TRow> {
  id: string;
  header: string;
  width?: string;
  render: (row: TRow) => ReactNode;
}

interface Props<TRow extends { id: string }> {
  icon: ComponentType<IconProps>;
  title: string;
  description: string;
  filter: ServiceAdminOrgAccessRequestFilter;
  onFilterChange: (filter: ServiceAdminOrgAccessRequestFilter) => void;
  isLoading: boolean;
  rows: TRow[];
  columns: Array<AccessRequestColumn<TRow>>;
}

export function AccessRequestsSection<TRow extends { id: string }>({
  icon: Icon,
  title,
  description,
  filter,
  onFilterChange,
  isLoading,
  rows,
  columns,
}: Props<TRow>) {
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [pageIndex, setPageIndex] = useState(0);

  const pageCount = Math.ceil(rows.length / pageSize);

  useEffect(() => {
    setPageIndex(0);
  }, [filter, pageSize]);

  const pageRows = useMemo(
    () => rows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [rows, pageIndex, pageSize],
  );

  return (
    <Box as="section" marginBottom="12">
      <HStack
        spacing="3"
        paddingBottom="3"
        marginBottom="5"
        width="80%"
        borderBottom="1px solid"
        borderColor="gray.50"
      >
        <Icon width="6" height="6" />
        <Heading as="h3" fontSize="2xl">
          {title}
        </Heading>
      </HStack>
      <chakra.p fontSize="md" color="gray.500" marginBottom="8">
        {description}
      </chakra.p>

      <Tabs
        index={accessRequestFilters.indexOf(filter)}
        onChange={(index) => onFilterChange(accessRequestFilters[index])}
        variant="line"
        colorScheme="blue"
      >
        <TabList marginBottom="6" width="max-content">
          {accessRequestFilters.map((value) => (
            <Tab key={value} fontWeight="semibold" color="gray.500">
              {t(filterLabels[value])}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
      >
        {isLoading ? (
          <Box padding="8">
            <PageLoader />
          </Box>
        ) : (
          <Table variant="simple" width="100%">
            <Thead>
              <Tr>
                {columns.map((column) => (
                  <Th
                    key={column.id}
                    width={column.width}
                    color="gray.700"
                    textTransform="none"
                    whiteSpace="nowrap"
                    letterSpacing="normal"
                  >
                    {column.header}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {pageRows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center" color="gray.500">
                    {t('noAccessRequests')}
                  </Td>
                </Tr>
              ) : (
                pageRows.map((row) => (
                  <Tr key={row.id}>
                    {columns.map((column) => (
                      <Td key={column.id} whiteSpace="nowrap">
                        {column.render(row)}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      <Box display="flex" marginTop="4">
        <Box flex="1">
          <PageSizeSelector
            label={t('showRowsPerPage')}
            options={PAGE_SIZES}
            value={pageSize}
            onChange={setPageSize}
          />
        </Box>
        <PaginationServerSide
          pageCount={pageCount}
          currentPageIndex={pageIndex}
          hasPreviousPage={pageIndex > 0}
          hasNextPage={pageIndex < pageCount - 1}
          goToPage={setPageIndex}
          goToPreviousPage={() => setPageIndex(pageIndex - 1)}
          goToNextPage={() => setPageIndex(pageIndex + 1)}
        />
      </Box>
    </Box>
  );
}
